
import { useState, useEffect, useRef } from 'react';
import Button from '../../../components/base/Button';
import { useTranslation } from '../../../hooks/useTranslation';

export default function ContactSection() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d479skihchb23d4eflgg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const socialLinks = [
    {
      name: 'Email',
      url: 'mailto:ikerperalta@proton.me',
      icon: 'ri-mail-line',
      label: 'ikerperalta@proton.me'
    },
    {
      name: 'LinkedIn',
      url: 'https://www.linkedin.com/in/ikerperaltacondes/',
      icon: 'ri-linkedin-line',
      label: 'LinkedIn Profile'
    },
    {
      name: 'X / Twitter',
      url: 'https://x.com/inteliker',
      icon: 'ri-twitter-x-line',
      label: '@inteliker'
    },
    {
      name: 'GitHub',
      url: 'https://github.com/inteliker',
      icon: 'ri-github-line',
      label: 'GitHub Profile'
    }
  ];

  return (
    <section ref={sectionRef} id="contact" className="py-20 bg-black relative overflow-hidden">
      {/* Background grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%2306b6d4' fill-opacity='0.1' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>

      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          {/* Section header */}
          <div className="text-center mb-16">
            <div className="font-mono text-cyan-400 text-sm mb-4">
              <span className="text-gray-500">root@cyberintel:~$</span> {t('contactTerminal')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-mono">
              <span className="text-cyan-400">&gt;</span> {t('contactTitle')}
            </h2>
            <p className="text-gray-400 font-mono text-lg max-w-2xl mx-auto">
              {t('contactSubtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact form */}
            <div className="bg-gray-900/50 border border-gray-700 p-8 rounded-lg">
              <div className="font-mono text-cyan-400 text-sm mb-6">
                <span className="text-gray-500">{t('sendMessage')}</span>
              </div>
              
              <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-gray-300 font-mono text-sm mb-2">
                    {t('nameLabel')} *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded text-white font-mono text-sm focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder={t('namePlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-gray-300 font-mono text-sm mb-2">
                    {t('emailLabel')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded text-white font-mono text-sm focus:border-cyan-500 focus:outline-none transition-colors"
                    placeholder={t('emailPlaceholder')}
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-gray-300 font-mono text-sm mb-2">
                    {t('messageLabel')} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 bg-black/50 border border-gray-600 rounded text-white font-mono text-sm focus:border-cyan-500 focus:outline-none transition-colors resize-none"
                    placeholder={t('messagePlaceholder')}
                  />
                  <div className="text-right text-gray-500 text-xs mt-1 font-mono">
                    {formData.message.length}/500
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-900/50 border border-green-500 text-green-400 p-4 rounded font-mono text-sm">
                    {t('successMessage')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-900/50 border border-red-500 text-red-400 p-4 rounded font-mono text-sm">
                    {t('errorMessage')}
                  </div>
                )}
                
                <Button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      {t('sending')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      {t('sendButton')}
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Social links */}
            <div className="space-y-8">
              <div className="bg-gray-900/50 border border-gray-700 p-8 rounded-lg">
                <div className="font-mono text-cyan-400 text-sm mb-6">
                  <span className="text-gray-500">{t('socialNetworks')}</span>
                </div>
                
                <div className="space-y-4">
                  {socialLinks.map((link, index) => (
                    <a
                      key={index}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-4 p-4 bg-black/30 border border-gray-700 hover:border-cyan-500/50 rounded-lg transition-all duration-300 hover:bg-gray-800/50 group cursor-pointer"
                    >
                      <div className="w-10 h-10 flex items-center justify-center text-cyan-400 text-xl group-hover:text-cyan-300 transition-colors">
                        <i className={link.icon}></i>
                      </div>
                      <div>
                        <div className="text-white font-mono text-sm group-hover:text-cyan-400 transition-colors">
                          {link.name}
                        </div>
                        <div className="text-gray-400 font-mono text-xs">
                          {link.label}
                        </div>
                      </div>
                      <div className="ml-auto text-gray-500 group-hover:text-cyan-400 transition-colors">
                        <i className="ri-external-link-line"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Terminal status */}
              <div className="bg-black/50 border border-gray-700 p-6 rounded-lg">
                <div className="font-mono text-cyan-400 text-sm mb-4">
                  <span className="text-gray-500">{t('systemStatus')}</span>
                </div>
                <div className="space-y-2 font-mono text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('status')}</span>
                    <span className="text-green-400">{t('statusOnline')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('responseTimeLabel')}</span>
                    <span className="text-cyan-400">{t('responseTime')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">{t('availabilityLabel')}</span>
                    <span className="text-cyan-400">{t('availability')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
